import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import API_URL from "../config";

function CandidateProfileView() {

    const { id } = useParams();

    const [candidate, setCandidate] = useState(null);

    const [unlocked, setUnlocked] = useState(false);

    const [subscription, setSubscription] =
        useState(null);

    const [loading, setLoading] =
        useState(true);

    const [unlocking, setUnlocking] =
        useState(false);


    // =====================================
    // GET LOGGED-IN RECRUITER
    // =====================================

    const user =
        JSON.parse(
            localStorage.getItem("user")
        );


    const recruiterId =
        user?.id;


    // =====================================
    // LOAD CANDIDATE
    // =====================================

    useEffect(() => {

        if (!recruiterId) {

            alert(
                "Recruiter session not found. Please login again."
            );

            return;

        }

        fetchCandidate();

    }, [id]);


    const fetchCandidate = async () => {

        try {

            setLoading(true);

            const res =
                await axios.get(
                    `${API_URL}/candidates/profile-view/${id}`,
                    {
                        params: {
                            recruiter_id:
                                recruiterId
                        }
                    }
                );


            setCandidate(
                res.data.candidate
            );


            setUnlocked(
                res.data.unlocked
            );


            setSubscription(
                res.data.subscription
            );

        }

        catch (err) {

            console.error(
                "CANDIDATE PROFILE ERROR:",
                err
            );

            alert(
                err.response?.data?.message ||
                "Unable to load candidate profile."
            );

        }

        finally {

            setLoading(false);

        }

    };


    // =====================================
    // UNLOCK CANDIDATE
    // =====================================

    const unlockCandidate = async () => {

        if (!recruiterId) {

            alert(
                "Recruiter session not found."
            );

            return;

        }


        try {

            setUnlocking(true);


            const res =
                await axios.post(
                    `${API_URL}/candidates/unlock`,
                    {
                        recruiter_id:
                            recruiterId,

                        candidate_id:
                            id
                    }
                );


            if (res.data.success) {

                setUnlocked(true);

                // Reload profile so phone,
                // email and resume are returned
                await fetchCandidate();

                return;

            }

        }

        catch (err) {

            console.error(
                "UNLOCK ERROR:",
                err
            );


            if (
                err.response?.data?.limitReached
            ) {

                alert(
                    "You have used all 10 candidate unlocks for this month. Please upgrade your plan."
                );

            }

            else {

                alert(
                    err.response?.data?.message ||
                    "Unable to unlock candidate."
                );

            }

        }

        finally {

            setUnlocking(false);

        }

    };


    // =====================================
    // LOADING
    // =====================================

    if (loading) {

        return (

            <div className="min-h-screen flex items-center justify-center">

                <div className="text-xl">
                    Loading Candidate...
                </div>

            </div>

        );

    }


    if (!candidate) {

        return (

            <div className="min-h-screen flex items-center justify-center">

                Candidate not found.

            </div>

        );

    }


    return (

<div className="min-h-screen bg-slate-50 px-3 py-4 sm:px-4 sm:py-6 md:px-8 md:py-12 overflow-x-hidden">

  <div className="max-w-6xl mx-auto w-full min-w-0">

    {/* Top Navigation */}
    <div className="flex items-start justify-between gap-3 mb-5 sm:items-center md:mb-6">
      <div>
        <p className="text-[10px] sm:text-xs md:text-sm text-teal-700 font-semibold mb-1 tracking-wide">
          HIRECURA / CANDIDATES
        </p>
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 break-words leading-tight">
          Candidate Profile
        </h1>
      </div>

      <Link
        to="/institution"
        className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-white border border-slate-200 text-slate-700 px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 md:py-3 rounded-xl text-sm md:text-base font-medium hover:bg-slate-100 transition"
      >
        <span aria-hidden="true">←</span>
        Back
      </Link>
    </div>

    {/* Candidate Header */}
    <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-100 rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-9 mb-5 md:mb-7">

      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 md:gap-6">

        {/* Profile Picture */}
        <div className="shrink-0">
          {candidate.profile_pic ? (
            <img
              src={`${API_URL.replace('/api', '')}/${candidate.profile_pic}`}
              alt="Candidate profile"
              className="w-20 h-20 sm:w-24 sm:h-24 md:w-36 md:h-36 rounded-full object-cover border-4 border-white shadow-md"
            />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-36 md:h-36 rounded-full bg-teal-100 border-4 border-white shadow-md flex items-center justify-center text-3xl md:text-4xl font-bold text-teal-700">
              {(candidate.name || "C").charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Candidate Information */}
        <div className="flex-1 min-w-0 w-full text-center sm:text-left">
          <span className="inline-block bg-white text-teal-700 text-[10px] sm:text-xs font-bold uppercase tracking-wider px-3 py-1.5 md:px-4 md:py-2 rounded-full border border-teal-100 mb-2 md:mb-3">
            Candidate Profile
          </span>

          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 break-words leading-tight">
            {candidate.name}
          </h2>

          <p className="hidden sm:block text-slate-600 text-sm sm:text-base md:text-lg mt-1.5 md:mt-2 break-words">
            {candidate.qualification || "Qualification not specified"}
          </p>

          <div className="hidden sm:flex flex-wrap justify-center sm:justify-start gap-2 mt-3 md:mt-5">
            <span className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs sm:text-sm text-slate-700 max-w-full break-words">
              <span className="font-semibold">Experience:</span>{" "}
              {candidate.experience_years ?? "Not specified"}
              {candidate.experience_years != null ? " years" : ""}
            </span>

            <span className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs sm:text-sm text-slate-700 max-w-full break-words">
              <span className="font-semibold">Location:</span>{" "}
              {candidate.location || "Not specified"}
            </span>
          </div>
        </div>

        {/* Status */}
        <div className="shrink-0">
          <span className={`inline-flex items-center px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs sm:text-sm font-semibold ${
            unlocked
              ? "bg-emerald-100 text-emerald-700"
              : "bg-amber-100 text-amber-700"
          }`}>
            {unlocked ? "Unlocked" : "Contact Locked"}
          </span>
        </div>

      </div>
    </div>

    {/* Main Content */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-7">

      {/* Left Column */}
      <div className="lg:col-span-2 space-y-4 sm:space-y-5 md:space-y-7 min-w-0">

        {/* Professional Details */}
        <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 shadow-sm p-4 sm:p-5 md:p-8 min-w-0">

          <div className="mb-4 md:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-slate-900">
              Professional Details
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Candidate qualifications and contact information.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-5">

            <div className="bg-slate-50 border border-slate-100 rounded-xl md:rounded-2xl p-3.5 sm:p-4 md:p-5 min-w-0">
              <p className="text-sm text-slate-500 mb-2">Qualification</p>
              <p className="font-semibold text-slate-800">
                {candidate.qualification || "Not specified"}
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-xl md:rounded-2xl p-3.5 sm:p-4 md:p-5 min-w-0">
              <p className="text-sm text-slate-500 mb-2">Experience</p>
              <p className="font-semibold text-slate-800">
                {candidate.experience_years ?? "Not specified"}
                {candidate.experience_years != null ? " years" : ""}
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-xl md:rounded-2xl p-3.5 sm:p-4 md:p-5 min-w-0">
              <p className="text-sm text-slate-500 mb-2">Phone Number</p>
              {unlocked ? (
                <p className="font-semibold text-slate-800 break-words">
                  {candidate.phone || "Not available"}
                </p>
              ) : (
                <p className="text-slate-400 text-sm">
                  Hidden — unlock candidate
                </p>
              )}
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-xl md:rounded-2xl p-3.5 sm:p-4 md:p-5 min-w-0">
              <p className="text-sm text-slate-500 mb-2">Email Address</p>
              {unlocked ? (
                <p className="font-semibold text-slate-800 break-all">
                  {candidate.email || "Not available"}
                </p>
              ) : (
                <p className="text-slate-400 text-sm">
                  Hidden — unlock candidate
                </p>
              )}
            </div>

            <div className="sm:col-span-2 bg-slate-50 border border-slate-100 rounded-xl md:rounded-2xl p-3.5 sm:p-4 md:p-5 min-w-0">
              <p className="text-sm text-slate-500 mb-2">Preferred Location</p>
              <p className="font-semibold text-slate-800">
                {candidate.location || "Not specified"}
              </p>
            </div>

          </div>
        </div>

        {/* Skills */}
        <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 shadow-sm p-4 sm:p-5 md:p-8 min-w-0">

          <h3 className="text-lg sm:text-xl font-bold text-slate-900">
            Skills & Expertise
          </h3>
          <p className="text-sm text-slate-500 mt-1 mb-6">
            Professional skills and areas of expertise.
          </p>

          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
            {candidate.skills &&
            candidate.skills.split(",").some(skill => skill.trim()) ? (
              candidate.skills
                .split(",")
                .filter(skill => skill.trim())
                .map((skill, index) => (
                  <span
                    key={index}
                    className="bg-teal-50 text-teal-800 border border-teal-100 px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs sm:text-sm font-medium break-words"
                  >
                    {skill.trim()}
                  </span>
                ))
            ) : (
              <p className="text-slate-400 text-sm">
                No skills added yet.
              </p>
            )}
          </div>
        </div>

        {/* Bio */}
        <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 shadow-sm p-4 sm:p-5 md:p-8 min-w-0">

          <h3 className="text-xl font-bold text-slate-900 mb-4">
            About Candidate
          </h3>

          <p className="text-sm sm:text-base text-slate-600 leading-6 sm:leading-7 whitespace-pre-line break-words">
            {candidate.bio || "No bio available."}
          </p>
        </div>

      </div>

      {/* Right Column */}
      <div className="space-y-4 sm:space-y-5 md:space-y-7">

        {/* Contact Unlock Card */}
        <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 shadow-sm p-4 sm:p-5 md:p-6 min-w-0">

          {!unlocked ? (
            <>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-50 rounded-xl md:rounded-2xl flex items-center justify-center mb-3 md:mb-5">
                <span className="text-xl font-bold text-teal-700">i</span>
              </div>

              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                Candidate Contact Details
              </h3>

              <p className="text-sm text-slate-500 mt-2 md:mt-3 leading-5 sm:leading-6">
                Unlock this candidate to access their phone number, email address and resume.
              </p>

              {subscription && (
                <div className="bg-slate-50 rounded-xl md:rounded-2xl p-3.5 sm:p-4 mt-4 md:mt-5">
                  <p className="text-sm text-slate-500">
                    Current Plan
                  </p>
                  <p className="font-semibold text-slate-800 mt-1">
                    {subscription.plan}
                  </p>

                  <div className="border-t border-slate-200 mt-3 pt-3">
                    <p className="text-sm text-slate-500">
                      Candidate Unlocks
                    </p>
                    <p className="font-semibold text-slate-800 mt-1">
                      {subscription.unlockLimit === "Unlimited"
                        ? "Unlimited"
                        : `${subscription.unlocksUsed} / ${subscription.unlockLimit}`}
                    </p>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={unlockCandidate}
                disabled={unlocking}
                className="w-full mt-4 md:mt-5 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-400 text-white text-sm sm:text-base font-semibold py-3 md:py-3.5 px-4 sm:px-5 rounded-xl transition"
              >
                {unlocking ? "Unlocking..." : "Unlock Candidate"}
              </button>
            </>
          ) : (
            <>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-50 rounded-xl md:rounded-2xl flex items-center justify-center mb-3 md:mb-5">
                <span className="text-xl font-bold text-emerald-700">✓</span>
              </div>

              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                Candidate Unlocked
              </h3>

              <p className="text-sm text-slate-500 mt-2 md:mt-3 leading-5 sm:leading-6">
                You can now access the candidate's available contact information and resume.
              </p>

              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5 md:p-4 mt-4 md:mt-5">
                <p className="text-sm font-medium text-emerald-800">
                  Contact access enabled
                </p>
              </div>
            </>
          )}
        </div>

        {/* Resume Card */}
        <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 shadow-sm p-4 sm:p-5 md:p-6 min-w-0">

          <h3 className="text-base sm:text-lg font-bold text-slate-900">
            Candidate Resume
          </h3>

          <p className="text-sm text-slate-500 mt-2 leading-6">
            Review the candidate's resume to learn more about their professional background.
          </p>

          {unlocked && candidate.resume_url ? (
            <a
              href={`${API_URL.replace('/api', '')}/${candidate.resume_url}`}
              target="_blank"
              rel="noreferrer"
              className="block text-center mt-4 md:mt-5 bg-white border border-teal-600 text-teal-700 hover:bg-teal-50 text-sm sm:text-base font-semibold px-4 sm:px-5 py-3 rounded-xl transition"
            >
              View Resume
            </a>
          ) : (
            <div className="mt-4 md:mt-5 bg-slate-50 rounded-xl p-3.5 md:p-4 text-center">
              <p className="text-sm text-slate-500">
                {unlocked
                  ? "No resume has been uploaded."
                  : "Unlock the candidate to access their resume."}
              </p>
            </div>
          )}
        </div>

      </div>
    </div>

    {/* Candidate Actions - hidden on mobile */}
    <div className="hidden sm:block bg-white border border-slate-200 shadow-sm rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 mt-5 md:mt-7">

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-5">

        <div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900">
            Candidate Actions
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Choose how to proceed with this candidate.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">

          {unlocked && candidate.resume_url && (
            <a
              href={`${API_URL.replace('/api', '')}/${candidate.resume_url}`}
              target="_blank"
              rel="noreferrer"
              className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm sm:text-base font-semibold px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl transition"
            >
              View Resume
            </a>
          )}

          <button
            type="button"
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm sm:text-base font-semibold px-5 sm:px-7 py-2.5 sm:py-3 rounded-xl transition"
          >
            Shortlist
          </button>

          <button
            type="button"
            className="bg-white border border-red-200 text-red-600 hover:bg-red-50 text-sm sm:text-base font-semibold px-5 sm:px-7 py-2.5 sm:py-3 rounded-xl transition"
          >
            Reject
          </button>

        </div>
      </div>
    </div>

  </div>
</div>
    );
}
export default CandidateProfileView;